import { Alert, Button, CommonProp, Heading, IconButton, IconMenuDocuments, Loading } from '@acin/ui-core';
import React, { useState } from 'react';
import { ChooseDocument } from '../../Documents/components/ChooseDocument';
import { useRegTextViewer } from '../../RegTextProvider';
import { useRegulationDetail } from '../../RegulationDetailProvider';
import { useRegulation } from '../../RegulationProvider';
import { useReviewObligations } from '../../ReviewObligationsProvider';
import { ReviewObligationsRegTextViewer } from './ReviewObligationsRegTextViewer';
import { useConfirmRegulationObligationMutation } from '../graphql/mutations/confirmRegulationObligation.generated';
import { IInternalRegulationObligationsQuery } from '../graphql/queries/internalRegulationObligations.generated';
import { useInternalRegulationObligationsReviewSummaryQuery } from '../graphql/queries/internalRegulationObligationsReviewSummary.generated';
import { createDocumentInstanceIdFilter, createRegulationIdFilter } from '../hooks/filterConstants';
import { ObligationRules } from './ObligationRules';
import { ObligationsList } from './ObligationsList';
import { ProcessMappings } from './ProcessMappings';
import * as SC from './ReviewObligations.styled';
import { RiskMappings } from './RiskMappings';

export interface ReviewObligationsProps extends CommonProp {
  className?: string;
  reviewedObligations?: number;
  totalObligations?: number;
}

export const ReviewObligations: React.FC<ReviewObligationsProps> = ({ className }) => {
  const [modalOpened, setModalOpened] = useState(false);
  const { regulationId } = useRegulationDetail();
  const { activeDocument } = useRegulation();
  const { activeObligation, onUpdateActiveObligation, selectedObligations, onToggleSelectedObligation } =
    useReviewObligations();
  const [confirmObligation, { loading: confirmLoading }] = useConfirmRegulationObligationMutation();

  const { regTextModalIsOpen, onToggleRegTextModal, onUpdateActiveSegmentId, onUpdateActiveFragmentId } =
    useRegTextViewer();

  const { data, loading, error } = useInternalRegulationObligationsReviewSummaryQuery({
    variables: {
      input: {
        filters: {
          groups: [createRegulationIdFilter(regulationId), createDocumentInstanceIdFilter(activeDocument?.id ?? '')],
        },
      },
    },
    skip: !activeDocument,
  });

  // Point the reg-text viewer at the active obligation's segment and first fragment.
  const activeSegmentId = activeObligation?.segmentId ?? undefined;
  const activeFragmentId = activeObligation?.fragment?.edges?.[0]?.node?.id;
  React.useEffect(() => {
    onUpdateActiveSegmentId(activeSegmentId);
    onUpdateActiveFragmentId(activeFragmentId);
  }, [activeSegmentId, activeFragmentId, onUpdateActiveSegmentId, onUpdateActiveFragmentId]);

  if (!activeDocument) {
    return <ChooseDocument modalOpened={modalOpened} onUpdateModalOpened={setModalOpened} />;
  }

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Alert title="Could not load obligations review summary" status="error" />;
  }

  const handleConfirmNext = async () => {
    const obligationId = Array.from(selectedObligations)[0]?.id;
    if (!obligationId) return;

    await confirmObligation({
      variables: {
        input: { obligationId },
      },
      refetchQueries: 'all',
      awaitRefetchQueries: true,
    });
  };

  const onObligationsQueryCompleted: (data: IInternalRegulationObligationsQuery) => void = (data) => {
    const activeObligationInData = data?.internalRegulationObligationsByDocument?.edges.find(
      ({ node }) => node.id === activeObligation?.id,
    );

    if (
      !activeObligationInData ||
      (!activeObligation && data?.internalRegulationObligationsByDocument?.edges.length > 0)
    ) {
      onUpdateActiveObligation(data.internalRegulationObligationsByDocument.edges[0].node);
    }
  };

  return (
    <SC.ReviewObligations className={className}>
      <SC.Columns>
        <SC.LeftColumn>
          <SC.ColumnHeader>
            <Heading size={2}>
              {data?.internalRegulationObligationsReviewSummary.reviewedObligationsCount} /{' '}
              {data?.internalRegulationObligationsReviewSummary.totalObligationsCount} Obligations Reviewed
            </Heading>
            <IconButton
              size="small"
              label="Regulation text"
              onClick={onToggleRegTextModal}
              icon={() => <IconMenuDocuments />}
            />
          </SC.ColumnHeader>
          <ObligationsList
            activeObligation={activeObligation}
            onUpdateActiveObligation={onUpdateActiveObligation}
            selectedObligations={selectedObligations}
            onToggleSelectedObligation={onToggleSelectedObligation}
            useReviewStatus
            onCompleted={onObligationsQueryCompleted}
          />
        </SC.LeftColumn>
        <SC.RightColumns>
          <SC.ColumnHeader>
            <Heading size={2}>{activeObligation?.title || 'Select an Obligation'}</Heading>
          </SC.ColumnHeader>
          <SC.SplitContent>
            <SC.RulesSection>
              <ObligationRules selectedObligationId={activeObligation?.id} />
            </SC.RulesSection>
            <SC.DetailsSection>
              <SC.DetailSection>
                <Heading size={2}>Risk Mappings</Heading>
                <RiskMappings selectedObligationId={activeObligation?.id} />
              </SC.DetailSection>
              <SC.DetailSection>
                <Heading size={2}>Process Mappings</Heading>
                <ProcessMappings selectedObligationId={activeObligation?.id} />
              </SC.DetailSection>
            </SC.DetailsSection>
          </SC.SplitContent>
        </SC.RightColumns>
      </SC.Columns>
      <SC.Footer>
        <SC.FooterContent>
          <SC.LeftButtons>
            <Button
              variant="borderless"
              label={`Confirm ${selectedObligations.length} Selected Obligations`}
              disabled={selectedObligations.length < 2}
            />
          </SC.LeftButtons>
          <SC.RightButtons>
            <Button variant="secondary" label="Run Oblimatch" disabled />
            <Button
              variant="secondary"
              label="Confirm & Next"
              disabled={selectedObligations.length === 0 || confirmLoading}
              onClick={handleConfirmNext}
            />
          </SC.RightButtons>
        </SC.FooterContent>
      </SC.Footer>
      {regTextModalIsOpen && <ReviewObligationsRegTextViewer />}
    </SC.ReviewObligations>
  );
};
